import { unitFor, useLocaleContext } from "../contexts/LocaleContext";

import fetchChart from "../api/ng/chart";
import convert, { normalizeUnit } from "../convert";
import { ISelectedSensor } from "../models/SelectedSensor";
import { SensorDisplayPrecip } from "../models/SensorDisplay";
import { isoToSeconds, toSeconds } from "../shared/duration";
import { useEffect, useState } from "react";

export interface ChartApiResponseMeta {
    units: { [measure: string]: string };
}
export interface ChartApiResponseData {
    ts: string;
    [measure: string]: any;
}

export interface ChartApiResponse {
    meta: ChartApiResponseMeta;
    data: ChartApiResponseData[];
}

export type Datum = {
    ts: Date;
    value: number;
}

const useChartData = (selected: ISelectedSensor) => {
    const { units } = useLocaleContext();
    const { display } = selected;
    const toUnit = unitFor(units, display.measure);

    const [response, setResponse] = useState<ChartApiResponse>()

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetchChart(selected);
            setResponse(response);
        }
        fetchData();
    }, [selected]);

    if (response) {
        const measure = SensorDisplayPrecip.is(display) ? "precip" : display.measure;
        const { meta, data } = response;

        const isIntensity =
            SensorDisplayPrecip.is(display) && display.measure === "intensity";
        const multiplier = isIntensity
            ? toSeconds({ hours: 1 }) / isoToSeconds(display.interval)
            : 1;

        const measure_ = Object.keys(meta.units).find(k => k.startsWith(measure));
        if (measure_ === undefined) {
            throw new Error(`Unhandled measure ${measure}`);
        }
        const fromUnit = normalizeUnit(isIntensity ? "in/h" : meta.units[measure_]);
        if (fromUnit === undefined) {
            throw new Error(`Unhandled unit ${fromUnit}`);
        }

        const measureData: Datum[] = data.map((d) => ({
            ts: new Date(d.ts),
            value: convert(d[measure] * multiplier).from(fromUnit).to(toUnit),
        }));
        return {
            ...data,
            source: {
                data: measureData,
                unit: toUnit
            },
        };
    }
};

export default useChartData;
